import type { StatsOverview } from "@reviewer73/shared";
import { GitMerge, GitPullRequestArrow } from "lucide-react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { mixHex } from "./charts/velocity-curve.js";
import { VelocityStreamFlow } from "./charts/velocity-stream-flow.js";
import { type StreamLane, VelocityStreams } from "./charts/velocity-streams.js";
import { useTheme } from "../hooks/use-theme.js";
import { repoName } from "../lib/format.js";
import { cssVar } from "../lib/utils.js";

type Velocity = StatsOverview["velocity"];

/** Row height per lane. Fixed so the ribbons line up with their names and numbers. */
const LANE_HEIGHT = 46;

/** Weeks per stream — matches the window the backend aggregates. */
const WEEKS = 8;

/** Stable empty fallback: a fresh `[]` would defeat the memoization below. */
const NO_STREAMS: Velocity["streams"] = [];

/** Name · stream · opened · diff · merged. Shared by the heading, the rows and the axis. */
const COLUMNS = "grid grid-cols-[minmax(6rem,9rem)_minmax(0,1fr)_3.25rem_4rem_3.25rem] gap-x-3";

/** "2026-08-03" → "3.8." — the Monday a week bucket starts on. */
function formatWeek(week: string) {
  const [, m, d] = week.split("-");
  return `${Number(d)}.${Number(m)}.`;
}

/** 8412 → "8,4k". Diff volumes span orders of magnitude; the column must not. */
function formatLines(lines: number) {
  if (lines < 1000) return String(lines);
  return `${(lines / 1000).toFixed(1).replace(".", ",")}k`;
}

/**
 * Overdrive: which projects are moving fastest right now, one stream each.
 *
 * Movement is raw merge-request activity — opened and merged — recorded from
 * the webhook whether or not a review ran, so a project nobody reviews still
 * shows its pace. A stream's thickness is the code volume of that week and its
 * particle density the merge-request count, which is the whole point of showing
 * both: a project moving in thirty small changes looks nothing like one moving
 * in three large ones, and the two read apart at a glance.
 *
 * All eight weeks share one scale across all five streams, so widths compare
 * directly. Rank is carried by tint — the leader in full magenta, the rest
 * fading toward it — never by a second color.
 */
export function Overdrive({ velocity, isLoading }: { velocity?: Velocity; isLoading: boolean }) {
  // Both layers take their colors from here rather than reading CSS variables
  // themselves, so ribbons and particles can never disagree and both repaint on
  // the same theme flip.
  const [theme] = useTheme();
  const past = cssVar("--chart-past");
  const today = cssVar("--chart-today");
  const border = cssVar("--border");
  void theme;

  // The streams are particles. Static ribbons are the fallback, not a layer
  // underneath — drawing both would be two graphs of the same numbers.
  const [flowFailed, setFlowFailed] = useState(false);
  const onUnsupported = useCallback(() => setFlowFailed(true), []);

  // Which lane and which week the cursor is over, plus where to put the readout.
  const [hover, setHover] = useState<{ lane: number; week: number; x: number; y: number } | null>(null);
  const handleHover = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const week = Math.max(0, Math.min(WEEKS - 1, Math.floor((x / rect.width) * WEEKS)));
    const lane = Math.max(0, Math.floor(y / LANE_HEIGHT));
    setHover({ lane, week, x, y });
  }, []);
  const reducedMotion =
    typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  const showRibbons = flowFailed || reducedMotion;

  const streams = velocity?.streams ?? NO_STREAMS;

  // Memoized deliberately, not as a habit: these are the WebGL layer's effect
  // dependencies, and a fresh array identity on every render would tear the
  // context down and rebuild it on each mouse move of the tooltip below.
  const { lanes, maxVolume, maxCount, laneColors } = useMemo(() => {
    // Diff volumes span orders of magnitude — a hundred changed lines next to
    // sixteen thousand. On a linear scale the largest project flattens every
    // other stream into a straight line, so the spread is driven by the square
    // root: still monotonic in changed code, but all five stay readable. The
    // exact figures are one column to the right for anyone who needs them.
    const computed: StreamLane[] = streams.map((stream) => ({
      projectPath: stream.projectPath,
      volume: stream.weeks.map((w) => Math.sqrt(w.changedLines)),
      count: stream.weeks.map((w) => w.opened + w.merged),
    }));
    return {
      lanes: computed,
      // One shared scale across every lane and week — otherwise each stream
      // would silently normalize to itself and compare nothing.
      maxVolume: Math.max(1, ...computed.flatMap((lane) => lane.volume)),
      maxCount: Math.max(1, ...computed.flatMap((lane) => lane.count)),
      laneColors: computed.map((_, i) =>
        computed.length > 1 ? mixHex(today, past, (i / (computed.length - 1)) * 0.75) : today,
      ),
    };
  }, [streams, today, past]);
  const trailColor = useMemo(() => mixHex(past, today, 0.5), [past, today]);

  if (isLoading) {
    return <div className="tile h-[300px] animate-pulse bg-muted" />;
  }

  if (streams.length === 0) {
    return (
      <div className="tile p-5 text-sm text-muted-foreground">
        Noch keine Bewegung — sobald in einem Projekt Merge Requests geöffnet oder gemerged werden,
        laufen hier die schnellsten fünf.
      </div>
    );
  }

  const hoveredStream = hover ? streams[hover.lane] : undefined;
  const hoveredBucket = hoveredStream?.weeks[hover!.week];
  const hoveredWeek = hoveredStream && hoveredBucket
    ? {
        projectPath: hoveredStream.projectPath,
        label: formatWeek(hoveredBucket.week),
        isCurrent: hoveredBucket.week === velocity?.currentWeek,
        opened: hoveredBucket.opened,
        merged: hoveredBucket.merged,
        changedLines: hoveredBucket.changedLines,
      }
    : null;

  return (
    <div className="tile overflow-hidden">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-5 pb-3 pt-5">
        <span className="dot dot--live self-center" style={{ "--dot": "var(--primary)" } as React.CSSProperties} />
        <h2 className="text-lg font-bold tracking-tight">Overdrive</h2>
        <p className="text-xs text-muted-foreground">
          Die fünf schnellsten Projekte über acht Wochen. Wie weit ein Strom streut, ist geänderter
          Code; wie dicht er fliesst, sind Merge Requests.
        </p>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[38rem] px-5 pb-5">
          {/* Column headings for the readout. The stream column stays unlabelled
              here — the footer axis below says what its horizontal span is. */}
          <div className={`${COLUMNS} items-baseline pb-1.5 text-xs text-muted-foreground`}>
            <span>Projekt</span>
            <span />
            <span className="text-right" title="Diese Woche geöffnete Merge Requests">
              <GitPullRequestArrow className="ml-auto h-3.5 w-3.5" aria-hidden="true" />
              <span className="sr-only">Geöffnet</span>
            </span>
            <span className="text-right">Diff</span>
            <span className="text-right" title="Diese Woche gemergte Merge Requests">
              <GitMerge className="ml-auto h-3.5 w-3.5" aria-hidden="true" />
              <span className="sr-only">Gemerged</span>
            </span>
          </div>

          <div className={COLUMNS} style={{ gridTemplateRows: `repeat(${lanes.length}, ${LANE_HEIGHT}px)` }}>
            {/* Every lane in one canvas: a single WebGL context for the whole
                field rather than one per row. */}
            <div
              className="relative col-start-2"
              style={{ gridRow: `1 / span ${lanes.length}` }}
              onMouseMove={handleHover}
              onMouseLeave={() => setHover(null)}
            >
              {/* Structure behind both modes: the eight week buckets the numbers
                  were summed over, and the current week shaded because it is
                  still running — otherwise its partial segment reads as every
                  project slowing down at once. A week is a column, not a
                  boundary, so eight weeks divide the width into eight. */}
              <div aria-hidden="true" className="pointer-events-none absolute inset-0">
                <div
                  className="absolute inset-0"
                  style={{
                    backgroundImage: `repeating-linear-gradient(to right, ${border} 0 1px, transparent 1px ${100 / WEEKS}%)`,
                  }}
                />
                <div
                  className="absolute inset-y-0 right-0 bg-muted/70"
                  style={{ left: `${((WEEKS - 1) / WEEKS) * 100}%` }}
                />
                {hover ? (
                  <div
                    className="absolute inset-y-0 bg-foreground/[0.06]"
                    style={{ left: `${(hover.week / WEEKS) * 100}%`, width: `${100 / WEEKS}%` }}
                  />
                ) : null}
              </div>
              {showRibbons ? (
                <VelocityStreams
                  lanes={lanes}
                  laneHeight={LANE_HEIGHT}
                  maxVolume={maxVolume}
                  laneColors={laneColors}
                />
              ) : (
                <VelocityStreamFlow
                  lanes={lanes}
                  laneHeight={LANE_HEIGHT}
                  maxVolume={maxVolume}
                  maxCount={maxCount}
                  leadColor={today}
                  trailColor={trailColor}
                  onUnsupported={onUnsupported}
                />
              )}

              {hoveredWeek ? (
                <div
                  className="pointer-events-none absolute z-10 w-max rounded-md border border-border bg-popover px-2.5 py-2 text-popover-foreground shadow-[0_6px_20px_rgb(0_0_0/0.12)]"
                  style={{
                    left: Math.min(hover!.x + 12, 320),
                    top: Math.max(0, Math.min(hover!.y - 8, LANE_HEIGHT * lanes.length - 92)),
                  }}
                >
                  <p className="font-mono text-xs font-semibold">{repoName(hoveredWeek.projectPath)}</p>
                  <p className="mb-1.5 font-mono text-xs text-muted-foreground">
                    {hoveredWeek.isCurrent ? "diese Woche, läuft noch" : `Woche ab ${hoveredWeek.label}`}
                  </p>
                  <dl className="grid grid-cols-[auto_auto] gap-x-3 font-mono text-xs tabular-nums">
                    <dt className="text-muted-foreground">geöffnet</dt>
                    <dd className="text-right font-semibold">{hoveredWeek.opened}</dd>
                    <dt className="text-muted-foreground">gemerged</dt>
                    <dd className="text-right font-semibold">{hoveredWeek.merged}</dd>
                    <dt className="text-muted-foreground">Diff</dt>
                    <dd className="text-right font-semibold">
                      {hoveredWeek.changedLines.toLocaleString("de-AT")}
                    </dd>
                  </dl>
                </div>
              ) : null}
            </div>

            {streams.map((stream, index) => (
              <Fragment key={stream.projectPath}>
                <Link
                  to="/projects"
                  className="col-start-1 flex items-center truncate font-mono text-sm font-semibold underline decoration-transparent underline-offset-4 transition-colors hover:decoration-current"
                  style={{ gridRow: index + 1 }}
                  title={stream.projectPath}
                >
                  {repoName(stream.projectPath)}
                </Link>
                <span
                  className="col-start-3 flex items-center justify-end font-mono text-sm tabular-nums"
                  style={{ gridRow: index + 1 }}
                >
                  {stream.current.opened}
                </span>
                <span
                  className="col-start-4 flex items-center justify-end font-mono text-sm font-semibold tabular-nums"
                  style={{ gridRow: index + 1 }}
                >
                  {formatLines(stream.current.changedLines)}
                </span>
                <span
                  className="col-start-5 flex items-center justify-end font-mono text-sm tabular-nums"
                  style={{ gridRow: index + 1 }}
                >
                  {stream.current.merged}
                </span>
              </Fragment>
            ))}
          </div>

          <div className={`${COLUMNS} pt-1.5`}>
            <span />
            <span className="col-start-2 flex justify-between font-mono text-xs text-muted-foreground">
              <span>vor 8 Wochen</span>
              <span>diese Woche, läuft noch</span>
            </span>
            <span className="col-span-3 col-start-3 text-right font-mono text-xs text-muted-foreground">
              bisher
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
